import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from '@lib/moment';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  FormControl,
  InputAdornment,
  Link,
  List,
  ListItem,
  ListItemText,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Box, Stack } from '@mui/system';
import { useForm } from 'react-hook-form';
import {
  AutocompleteElement,
  DateTimePickerElement,
  FormContainer,
  SelectElement,
  TextFieldElement
} from 'react-hook-form-mui';
import { format, useNumberFormat } from '@react-input/number-format';

import TextAreaLimiter from '@components/common/TextAreaLimiter';
import { useConfirm } from '@components/common/hooks/useConfirm';
import useErrorHandling from '@components/common/hooks/useErrorHandling';
import useStaffUserLookup from '@components/common/hooks/useStaffUserLookup';
import useSessionParticipantsQuery from '@components/common/hooks/api/useSessionParticipantsQuery';
import useStaffUsersQuery from '@components/common/hooks/api/useStaffUsersQuery';
import AutocompleteMultipleElement from '@components/common/HookFormMui/AutocompleteMultipleElement';
import CheckboxButtonGroup from '@components/common/HookFormMui/CheckboxButtonGroup';
import RadioButtonGroup from '@components/common/HookFormMui/RadioButtonGroup';
import EventDemographicFormInstructions
  from '@components/ContentComponents/Events/Demographics/EventDemographicsFormInstructions';
import EventDemographicsFormFields from '@components/ContentComponents/Events/Demographics/EventDemographicsFormFields';
import { keyBehaviors, NILL } from '@lib/constants';
import { toUserOption } from '@lib/user';
import {
  formatDateTime,
  formatFormQuestions,
  getValuesFromAnswers,
  maybeValue,
  prefillAnswers,
  preventNonNumericKeyPress,
  validatePositiveInteger
} from '@lib/utils';
import {
  getDemographicsInputValues,
  hasDemographicData,
  shouldCollectDemographics,
  shouldTrackParticipants,
  toLessonOption
} from '../../eventFunctions';
import { Link as RouterLink } from 'react-router-dom';
import { getLessonName } from '@components/Pages/Events/Sessions/sessionFunctions';
import AutocompleteOther from '@components/common/HookFormMui/AutocompleteOther';
import useDemographicOptionsQuery from "@components/common/hooks/api/useDemographicOptionsQuery";

const EditCell = styled(TableCell)({
  border: `1px solid #0288d1 !important`, // info.main
});

const CompleteRow = ({ n, name, colA, colB, colC }) => (
  <TableRow id={`${name}-row`}>
    <TableCell id={`${name}-colA`}>{`${n}. ${colA}`}</TableCell>
    <TableCell id={`${name}-colB`}>{colB}</TableCell>
    <EditCell id={`${name}-colC`}>{colC}</EditCell>
  </TableRow>
);

const TableComp = styled(Table)(({ theme }) => ({
  marginBottom: '32px',
  '& .MuiTableCell-root': {
    fontSize: theme.typography.pxToRem(16),
    padding: '10px 12px',
    '&.MuiTableCell-head': {
      padding: '16px',
    }
  }
}));

const TableHeadRow = styled(TableRow)({
  '& .MuiTableCell-root': {
    backgroundColor: '#f4f4f4',
    '&:first-of-type': {
      fontWeight: 'bold',
    },
  },
});

const noUnderline = {
  '& .MuiInputBase-root.MuiInput-root': {
    '&::before, &.MuiInput-underline::after': { borderBottom: 'none' },
  },
};

const NumberField = styled(TextFieldElement)({
  '.MuiInput-root': {
    maxWidth: '150px'
  },
});

const ParticipantAlert = () => (
  <Grid container spacing={0} noGutters width="100%" sx={{ marginBottom: '8px' }}>
    <Grid xs={12}>
      <Alert
        severity="warning"
        sx={{
          mt: '15px',
          width: '100%',
        }}>
        Zero participants is not allowed.
        If there are zero participants for this session, please cancel the session and select &quot;No participants
        showed up.&quot;
      </Alert>
    </Grid>
  </Grid>
);

const WORD_LIMIT = 250;
const OTHER_NEW = 'other:new';
const OTHER_PLANNED = 'other:planned';

const getTextAreaHelperText = (words) => `${WORD_LIMIT} word limit (${words} word${words === 1 ? '' : 's'} used)`;

const CompleteSessionForm = ({
  session,
  previous_sessions,
  previous_participant_actions,
  pearsData,
  boothTopics,
  questions,
  submitting,
  onSubmit,
  handleUpdateParticipants,
}) => {
  const [wasSubmitting, setWasSubmitting] = useState(false);
  const [demographicsExpanded, setDemographicsExpanded] = useState(false);

  const pickerOpenStart = useRef(false);
  const theme = useTheme();

  const currencyFormatRef = useNumberFormat({
    currency: 'USD',
    locales: 'en',
    maximumFractionDigits: 2,
  });

  const { data: demographicOptions, ...demographicOptionsQuery } = useDemographicOptionsQuery();

  const completed = session.completed;

  const getLang = useCallback((langId) => {
    const lang = pearsData.languages.find((l) => (l.id === langId || l.label === langId));
    return lang?.label || lang?.id || langId;
  }, [pearsData]);

  // Determine whether participants were entered individually.
  //
  // This means that there are any rows in the 'participants' table in the DB for this event. These may be added
  // using the staff mobile app, or by entering them into the Finalize Participants page while completing a session.
  //
  // If participants were tracked individually, the demographic counts are calculated automatically when the
  // participant list is finalized, and we disable some fields on this form.
  const { data: participants } = useSessionParticipantsQuery(session.event_id);
  const hasIndividualParticipants = shouldTrackParticipants(session.event) && participants?.length > 0;

  const completedAnswers = useMemo(() => {
    if (!questions) {
      return {};
    }
    if (!completed?.completed_answers || !Object.keys(completed?.completed_answers).length) {
      // There were no existing answers in the DB. Pre-fill the answers with the values from the session.
      return prefillAnswers(questions, session.event?.event_answers);
    }
    return getValuesFromAnswers(completed?.completed_answers, questions);
  }, [questions, completed?.completed_answers, session.event?.event_answers]);

  const formQuestions = useMemo(() =>
      formatFormQuestions(questions, completedAnswers),
    [questions, completedAnswers]);

  const { data: staffUsers } = useStaffUsersQuery();
  const { getUserName, mapCollaboratorsToOptions } = useStaffUserLookup();
  const staffUserOptions = useMemo(() => {
    if (!staffUsers) return [];
    return staffUsers.filter(u => u.email !== session.event.created_by).map(toUserOption);
  }, [staffUsers]);

  const values = useMemo(() => {
    const eventDemographicsValues = getDemographicsInputValues(session.event, demographicOptions);

    // There can be a "stub" row in the `sessions_completed` table, which is created by the Finalize Participants page.
    // Stub rows can be detected by a null start date.
    const isStub = !!completed && !completed.start;

    if (completed && !isStub) {
      return {
        as_planned: maybeValue(completed.as_planned),
        as_planned_detail: completed.as_planned_detail,
        participant_count: completed.participant_count,
        participant_actions: completed.participant_actions,
        participant_has_story: maybeValue(completed.participant_has_story),
        participant_permission: maybeValue(completed.participant_permission),
        participant_behaviors: completed.participant_behaviors ? completed.participant_behaviors.efnep : [],
        participant_story: completed.participant_story,
        lessonID: completed.lesson_id,
        lesson: completed.lesson ? {
          ...toLessonOption(completed.lesson),
          ...(completed.lesson.custom && {
            optionKey:
              completed.booth_topic_name &&
              completed.booth_topic_name === session.booth_topic_name
                ? OTHER_PLANNED
                : OTHER_NEW,
          }
        )} : null,
        lesson_name: completed.lesson_name,
        booth_topic_name: completed.booth_topic_name,
        start: completed.start ? moment(completed.start) : moment(session.start),
        end: completed.end ? moment(completed.end) : moment(session.end),
        delivery_method: completed.delivery_method,
        delivery_language: completed.delivery_language?.map(getLang) ?? null,
        material_language: completed.material_language?.map(getLang) ?? null,
        delivery_location: completed.delivery_location,
        demonstration: maybeValue(completed.demonstration),
        recipes: completed.recipes,
        collaborators: mapCollaboratorsToOptions(completed.collaborators),
        donations_received: maybeValue(completed.donations_received),
        donations_total: completed.donations_total ? format(completed.donations_total || 0, {
          currency: 'USD',
          locales: 'en',
          maximumFractionDigits: 2,
        }) : null,
        // evaluation_tool: completed.evaluation_tool,
        comments: completed.comments,
        ...eventDemographicsValues,

        ...completedAnswers,
      };
    } else {
      // No "session.completed" object exists, or the existing object is a stub.
      // Fill in some default values based on the session data.
      const values = {
        start: moment(session.start),
        end: moment(session.end),
        delivery_method: session.event.event_type === 'BOOTH' ? ['In Person'] : session.delivery_method,
        delivery_location: session.delivery_location,
        participant_behaviors: [],
        collaborators: mapCollaboratorsToOptions(session.collaborators),
        ...eventDemographicsValues,

        ...completedAnswers,
      };
      // If a stub row was created when finalizing participants, we want the participant count from it.
      if (isStub) {
        values.participant_count = completed.participant_count;
      }
      return values;
    }
  }, [session, completed, getLang, formQuestions, demographicOptions]);

  const form = useForm({
    mode: 'onSubmit',
    values,
  });

  const {
    handleSubmit,
    control,
    trigger,
    watch,
    setValue,
    getValues,
    clearErrors,
    formState: { errors, isSubmitting },
  } = form;

  const watchAsPlanned = watch('as_planned');
  const watchDemo = watch('demonstration');
  const watchDonations = watch('donations_received');
  const watchEnterDemographics = watch('enter_demographics');
  const watchHasStory = watch('participant_has_story');

  useEffect(() => {
    setDemographicsExpanded(watchEnterDemographics === 'yes');
  }, [watchEnterDemographics]);

  useErrorHandling({ errors, isSubmitting, wasSubmitting, setWasSubmitting });

  const hasPreviousSessions = previous_sessions.length > 0;
  const hasPreviousActions = previous_participant_actions.length > 0;

  useEffect(() => {
    // Clear "as planned detail" field when "as planned" is set to "yes"
    if (watchAsPlanned === 'true') {
      setValue('as_planned_detail', null);
    }
  }, [watchAsPlanned, setValue]);

  useEffect(() => {
    // Clear "participant story" fields when "has story" is set to "no"
    if (watchHasStory === 'false') {
      setValue('participant_permission', null);
      setValue('participant_behaviors', null);
      setValue('participant_story', null);
    }
  }, [watchHasStory, setValue]);

  useEffect(() => {
    // Clear donation amount field when "donations received" is set to "no"
    if (watchDonations === 'false') {
      setValue('donations_total', null);
    }
  }, [watchDonations, setValue]);

  const donationsReceived = watchDonations === 'true';
  const hasDemonstration = session.demonstration || watchDemo === 'true';

  const onSubmitClick = (mode) => {
    // This form has 3 modes:
    // 'save': Session not yet completed; save partial values with no validation
    // 'complete': Save with validation and set session to COMPLETED, also set completed date and user
    // 'update': Update data for a session that's already completed, with validation
    if (mode === 'complete' || mode === 'update') {
      // Save with normal form validation
      const result = handleSubmit((values) => {
        values.save = mode;
        return onSubmit(values);
      });
      // expand demographics sub-form if any of its fields have errors
      if ((errors.age || errors.gender || errors.race || errors.source) && !demographicsExpanded) {
        setDemographicsExpanded(true);
      }
      return result;
    } else {
      // Save data with no validation (used for saving partial data)
      return () => {
        const values = getValues();
        values.save = mode;
        return onSubmit(values);
      };
    }
  };

  const handleStartChange = (value) => {
    if (!pickerOpenStart.current) {
      handleStartAccept(value);
    } else {
      if (!value) return true;
      trigger('start').then();
      trigger('end').then();
    }
  };

  const handleStartAccept = (value) => {
    if (!value) return true;
    const eventEnd = getValues('end');
    let newEnd = (eventEnd && eventEnd.isValid()) ? eventEnd.clone() : null;
    if ((!eventEnd || !eventEnd.isValid()) && value && value.isValid()) {
      newEnd = value.clone();
      newEnd.add(1, 'hours');
      setValue('end', newEnd);
    } else if (newEnd && value && value.isValid()) {
      newEnd.set({
        year: value.get('year'),
        month: value.get('month'),
        date: value.get('date'),
      }).format();
      setValue('end', newEnd);
    }
    trigger('start').then();
    trigger('end').then();
  };

  const lessonOptions = useMemo(() => {
    if (!session) {
      return [];
    }
    if (session.event.event_type === 'BOOTH') {
      const topics = boothTopics.filter((c) => !c.custom).map(toLessonOption);
      const other = boothTopics.find((t) => t.custom);   // the real Other row

      if (other) {
        // SEMS-2057 Scenario 1 lists the session's user-defined topic before "Other".
        if (session.lesson?.custom && session.booth_topic_name) {
          topics.push({
            ...toLessonOption(other),
            optionKey: OTHER_PLANNED,
            label: session.booth_topic_name,
            customText: session.booth_topic_name,
          });
        }
        topics.push({ ...toLessonOption(other), optionKey: OTHER_NEW });
      }
      return topics;
    } else {
      return session.event?.curriculum?.lessons?.map((c) => ({
        id: c.id,
        label: c.lesson_name,
        audience: c.audience,
        custom: c.custom,
      }));
    }
  }, [boothTopics, session]);

  const confirm = useConfirm();

  const ethicalPracticesModal = () => confirm({
    title: 'Ask permission first!',
    content: (
      <Box sx={{ width: '100%' }} key="content">
        <p>
          {`It's important to remember that participants' stories belong to them, not us. It's THEIR story, even when
          it demonstrates something positive about our program.`}
        </p>
        <p>
          <strong>{`If you've heard a great success story, ask the participant if it's OK to share it.`}</strong>
        </p>
        <p>
          {`Let them know that sharing participant successes helps our partners and funders see how we make a difference
          for real people in the community. This helps us keep the program going over time. You can also share that
          participant stories are sometimes used in program reports and press releases, but names are never included
          unless they want to be quoted directly.`}
        </p>
        <p>
          <strong>{`If the participant doesn't want their story shared, that's OK (celebrate with them anyway!),
            and DO NOT report their story in Events Manager.`}</strong>
        </p>
      </Box>
    ),
    confirmationButtonProps: {
      variant: 'contained',
    },
    confirmationText: 'OK',
    hideCancelButton: true,
  });

  const PreviousActions = () => {
    if (!hasPreviousActions && !hasPreviousSessions) {
      return null;
    }
    return (
      <Box sx={{
        backgroundColor: theme.palette.lightblue.main,
        padding: '10px 16px',
        marginLeft: '16px',
      }}>
        <Typography variant="h6" fontFamily="SourceSans">Actions taken so far:</Typography>
        <List sx={{
          padding: 0,
          '& .MuiListItem-root': {
            padding: 0,
          },
          '& .MuiListItemText-root': {
            margin: 0,
            fontSize: '1rem',
            fontFamily: 'SourceSans',
          }
        }}>
          {previous_participant_actions.map((action, i) => (
            <ListItem key={`action-${i}`}>
              <ListItemText primary={action} sx={{ fontStyle: 'italic' }} />
            </ListItem>
          ))}
          {!hasPreviousActions && (
            <ListItem>
              <ListItemText primary="No actions found" />
            </ListItem>
          )}
        </List>
      </Box>
    );
  };

  const languages = pearsData?.languageLabels;

  const isClass = session.event.event_type === 'CLASS';

  const rows = [];

  const topRows = useMemo(() => {
    const tRows = [];
    tRows.push(
      (i) => <RadioButtonGroup
        key={`row-as_planned`}
        id="field-as_planned"
        name="as_planned"
        label={`${i}. Overall, did this session go the way you thought it would?`}
        control={control}
        fullWidth
        required
        options={[
          { label: 'Yes', id: 'true' },
          { label: 'No', id: 'false' },
        ]}
      />
    );

    if (watchAsPlanned === 'false') {
      const label = 'Please share a bit more about why you selected "No" above.';
      tRows.push(
        (i) =>
          <TextAreaLimiter
            key={`row-as_planned_detail`}
            id="field-as_planned_detail"
            name="as_planned_detail"
            getHelperText={getTextAreaHelperText}
            wordLimit={WORD_LIMIT}
            control={control}
            rows={3}
            fullWidth
            required
            staticLabel
            label={`${i}. ${label}`}
          />
      );
    }
    return tRows;
  }, [watchAsPlanned, control]);

  const needsAndActions = [];
  const needsLabel1 = formQuestions?.participant_needs?.label;
  const needsLabel2 = hasPreviousSessions ?
    'Write any new actions you have taken to meet the specific needs of this group.' :
    'What actions have you taken to meet the specific needs of this group?';

  needsAndActions.push(
    (i) => <CheckboxButtonGroup
      key={`row-participant_needs`}
      label={`${i}. ${needsLabel1}`}
      id="field-participant_needs"
      name="participant_needs"
      control={control}
      required
      fullWidth
      textFieldRequired
      textFieldInline
      options={formQuestions?.participant_needs?.options ?? []}
    />
  );

  needsAndActions.push((i) => (
    <FormControl
      error={!!errors?.participant_actions}
      required={!hasPreviousSessions}
      sx={{ width: '100%' }}
    >
      <TextAreaLimiter
        id="field-participant_actions"
        name="participant_actions"
        key={`row-participant_actions`}
        getHelperText={getTextAreaHelperText}
        wordLimit={WORD_LIMIT}
        control={control}
        rows={3}
        fullWidth
        required={!hasPreviousSessions}
        aria-describedby="field-participant_actions-label"
        staticLabel
        label={`${i}. ${needsLabel2}`}
        afterLabel={<PreviousActions />}
      />
    </FormControl>
  ));

  const participantFieldLabel = useMemo(() => {
    if (!session?.event) {
      return '';
    }
    if (session.event.event_type === 'BOOTH') {
      return 'How many participants did you actively engage during this booth?';
    }
    switch (session.event.curriculum?.audience) {
      case 'Youth':
        return 'How many youth participants attended this session?';
      case 'Adults':
        return 'How many adult participants attended this session?';
      case 'Family':
        return 'How many adult and youth participants attended this session? Only include youth who actively participated in the session.';
      default:
        return 'How many participants attended this session?';
    }
  }, [session.event]);

  const participantCount = watch('participant_count');

  // If demographics can be entered for this event, we show a form field asking if the user is ready to enter them.
  const askForDemographics = shouldCollectDemographics(session.event) && !hasDemographicData(session.event);

  // If ready to enter demographics, or demographics have previously been entered for the event, we show the
  // sub-form containing the demographics fields.
  const showDemographicsFields = askForDemographics ?
    watchEnterDemographics === 'yes' :
    (shouldCollectDemographics(session.event) && !!session.event.demographics);

  const participantCountFields = [];

  participantCountFields.push((i) => (
    <FormControl
      error={!!errors?.participant_count}
      required
      sx={{ width: '100%' }}
    >
      <NumberField
        id="field-participant_count"
        key="field-participant_count"
        name="participant_count"
        label={`${i}. ${participantFieldLabel}`}
        control={control}
        type="number"
        required
        validation={{
          ...validatePositiveInteger,
          min: {
            value: 1,
            message: 'Please enter a number greater than zero.'
          },
        }}
        disabled={hasIndividualParticipants}
        onKeyDown={preventNonNumericKeyPress}
        data-cy="participant-count-field"
        aria-describedby="field-participant_count-label"
      />
      {participantCount === 0 && (
        <ParticipantAlert />
      )}
    </FormControl>
  ));

  if (askForDemographics) {
    // Note: This field is not saved to the DB, and the answer will not "stick" if you leave the
    // form and return to it later.
    participantCountFields.push(
      (i) => [
        <RadioButtonGroup
          key={`row-enter_demographics`}
          id="field-enter_demographics"
          name="enter_demographics"
          label={`${i}. Are you ready to enter the demographic data for this group?`}
          control={control}
          fullWidth
          required
          options={[
            { label: 'Yes', id: 'yes' },
            { label: 'No', id: 'no' },
          ]}
        />,
        showDemographicsFields && (
          <Box sx={{ mb: 5 }}>
            <Accordion
              sx={{ mb: 5 }}
              expanded={demographicsExpanded}
              onChange={(ev, isExpanded) => setDemographicsExpanded(isExpanded)}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="demographics-content"
                id="demographics-header"
              >
                <Typography variant="h3" sx={{ fontSize: '1rem' }}>View/Edit Demographics</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <EventDemographicFormInstructions event={session.event} />
                <EventDemographicsFormFields form={form} event={session.event} />
              </AccordionDetails>
            </Accordion>
          </Box>
        ),
      ],
    );
  } else if (showDemographicsFields) {
    participantCountFields.push(
      (i) =>
        <Box sx={{ mb: 5 }}>
          <Accordion
            sx={{ mb: 5 }}
            expanded={demographicsExpanded}
            onChange={(ev, isExpanded) => setDemographicsExpanded(isExpanded)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="demographics-content"
              id="demographics-header"
            >
              <Typography variant="h3" sx={{ fontSize: '1rem' }}>View/Edit Demographics</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <EventDemographicFormInstructions event={session.event} />
              <EventDemographicsFormFields form={form} event={session.event} />
            </AccordionDetails>
          </Accordion>
        </Box>
    );
  }

  participantCountFields.push(
    (i) => <RadioButtonGroup
      key={`row-participant_has_story`}
      id="field-participant_has_story"
      name="participant_has_story"
      label={`${i}. Do you have a participant success story to share from this session?`}
      control={control}
      fullWidth
      required
      options={[
        { label: 'Yes', id: 'true' },
        { label: 'No', id: 'false' },
      ]}
    />
  );

  if (watchHasStory === 'true') {
    participantCountFields.push(
      (i) => <RadioButtonGroup
        key={`row-participant_permission`}
        id="field-participant_permission"
        name="participant_permission"
        label={
          <>
            <span>{i}. Did you receive verbal permission from the participant to share this story?</span>
            <Link className="label-link" style={{ order: 1, marginLeft: '8px' }} onClick={ethicalPracticesModal}>Ethical Story Collection
              Practices</Link>
          </>
        }
        control={control}
        fullWidth
        required
        options={[
          { label: 'Yes', id: 'true' },
          { label: 'No', id: 'false' },
        ]}
      />
    );

    participantCountFields.push(
      (i) => <CheckboxButtonGroup
        key={`row-participant_behaviors`}
        label={`${i}. Select all Key Behaviors related to this story:`}
        id="field-participant_behaviors"
        name="participant_behaviors"
        maxColumns={2}
        control={control}
        required
        fullWidth
        textFieldRequired
        textFieldInline
        options={keyBehaviors}
      />
    );

    participantCountFields.push(
      (i) =>
        <TextAreaLimiter
          key={`row-participant_story`}
          id="field-participant_story"
          name="participant_story"
          control={control}
          getHelperText={getTextAreaHelperText}
          wordLimit={WORD_LIMIT}
          staticLabel
          label={
            <>
              <span>{i}. Please share the participant story below.</span>
              <span className="label-link" style={{ order: 1, marginLeft: '8px' }}>
                <Link target="_blank" href="/Success_Stories_Guidance_EFNEP.pdf">Example of a good success story</Link>
              </span>
            </>
          }
          rows={4}
          fullWidth
          required
        />
    );
  }

  if (session.event?.curriculum?.custom) {
    // Event has a custom/other curriculum. Show the custom lesson name field.
    // This is for lessons only. For booths with custom names, see AutocompleteOther below.
    rows.push(
      (i) => <CompleteRow
        n={i}
        key={`row-lesson_name`}
        name="lesson_name"
        colA={'Lesson Taught'}
        colB={getLessonName(session)}
        colC={
          <TextFieldElement
            id="field-lesson_name"
            name="lesson_name"
            variant={'standard'}
            control={control}
            required
            fullWidth
            sx={noUnderline}
          />
        }
      />
    )
  } else {
    // Classes with non-custom curricula, and all booths.
    rows.push(
      (i) => <CompleteRow
        n={i}
        key={`row-lesson`}
        name="lesson"
        colA={isClass ? 'Lesson Taught' : 'Booth Topic'}
        colB={getLessonName(session)}
        colC={
          <AutocompleteOther
            id="field-lesson"
            name="lesson"
            placeholder={'Choose curriculum'}
            textFieldName={'booth_topic_name'}
            textFieldProps={{ variant: 'standard' }}
            control={control}
            fullWidth
            required
            options={lessonOptions}
            autocompleteProps={{
              isOptionEqualToValue: (o, v) => (o?.optionKey ?? o?.id) === (v?.optionKey ?? v?.id),
              sx: noUnderline,
            }}
          />
        }
      />
    )
    ;
  }

  rows.push(
    (i) => <CompleteRow
      n={i}
      key={`row-start`}
      name="start"
      colA="Start Date & Time"
      colB={formatDateTime(session.start)}
      colC={
        <DateTimePickerElement
          id="field-start"
          name="start"
          // label={'What is the planned start date & time for this session?'}
          placerholder=""
          control={control}
          required
          validation={{
            validate: {
              /** @param {AdapterMoment} value **/
              range: (value) => {
                const endDate = getValues('end');
                if (!value || !endDate) return true;
                const valid = !value.isAfter(endDate);
                return valid || 'Start Date & Time must be less than End Date & Time';
              }
            }
          }}
          onOpen={() => {
            pickerOpenStart.current = true;
          }}
          onClose={() => {
            pickerOpenStart.current = false;
          }}
          onAccept={handleStartAccept}
          onChange={handleStartChange}
          inputProps={{
            variant: 'standard',
            fullWidth: true,
            id: 'field-start',
            sx: noUnderline,
          }}
        />
      }
    />
  );

  rows.push(
    (i) => <CompleteRow
      n={i}
      key={`row-end`}
      name="end"
      colA="End Date & Time"
      colB={formatDateTime(session.end)}
      colC={
        <DateTimePickerElement
          id="field-end"
          name="end"
          // label={'What is the planned end date & time for this session'}
          // helperText={'End Date & Time must be greater than Start Date & Time.'}
          control={control}
          required
          validation={{
            validate: {
              /** @param {AdapterMoment} value **/
              range: (value) => {
                const eventStart = getValues('start');
                if (!value || !eventStart) return true;
                const valid = value.isAfter(eventStart);
                return valid || 'End Date & Time must be greater than Start Date & Time';
              }
            }
          }}
          onChange={() => {
            trigger('start').then();
            trigger('end').then();
          }}
          inputProps={{
            variant: 'standard',
            fullWidth: true,
            id: 'field-end',
            sx: noUnderline,
          }}
        />
      }
    />
  );

  if (isClass) {
    rows.push(
      (i) => <CompleteRow
        n={i}
        key={`row-delivery_method`}
        name="delivery_method"
        colA="Delivery Method"
        colB={session.delivery_method?.join(', ')}
        colC={
          <AutocompleteMultipleElement
            id="field-delivery_method"
            name="delivery_method"
            control={control}
            textFieldProps={{variant: 'standard'}}
            required={isClass}
            fullWidth
            // label="What method will be used to deliver this program? Select all that apply."
            onChange={() => null}
            options={[
              'In Person',
              'Virtual/Live Online',
            ]}
            autocompleteProps={{
              disabled: !isClass,
              sx: noUnderline,
            }}
          />
        }
      />
    );
  }

  if (session.delivery_method?.includes('In Person')) {
    rows.push(
      (i) => <CompleteRow
        n={i}
        key={`row-delivery_location`}
        name="delivery_location"
        colA="Session Delivery Site"
        colB={session.delivery_location || NILL}
        colC={
          <AutocompleteElement
            id="field-delivery_location"
            name="delivery_location"
            // label="Where will this session be delivered?"
            control={control}
            fullWidth
            required={session.delivery_method?.includes('In Person')}
            variant="standard"
            options={pearsData?.partners || []}
            autocompleteProps={{
              isOptionEqualToValue: (option, newValue) => {
                return option === newValue;
              },
              sx: noUnderline,
            }}
            textFieldProps={{
              variant: 'standard',
            }}
          />
        }
      />
    );
  }

  rows.push(
    (i) => <CompleteRow
      n={i}
      key={`row-delivery_language`}
      name="delivery_language"
      colA="Session Delivery Language(s)"
      colB={session.delivery_language?.join(', ')}
      colC={
        <AutocompleteMultipleElement
          id="field-delivery_language"
          name="delivery_language"
          control={control}
          textFieldProps={{variant: 'standard'}}
          required
          fullWidth
          options={languages}
          onChange={() => null}
          autocompleteProps={{
            sx: noUnderline,
          }}
        />
      }
    />
  );

  rows.push(
    (i) => <CompleteRow
      n={i}
      key={`row-material_language`}
      name="material_language"
      colA="Participant Material Language(s)"
      colB={session.material_language?.join(', ')}
      colC={
        <AutocompleteMultipleElement
          id="field-material_language"
          name="material_language"
          textFieldProps={{ variant: 'standard' }}
          control={control}
          required
          fullWidth
          options={languages}
          onChange={() => null}
          autocompleteProps={{
            sx: noUnderline,
          }}
        />
      }
    />
  );

  rows.push(
    (i) => <CompleteRow
      n={i}
      key={`row-demonstration`}
      name="demonstration"
      colA="Food Demonstration/Taste Test Included"
      colB={session.demonstration ? 'Yes' : 'No'}
      colC={
        <SelectElement
          id="field-demonstration"
          name="demonstration"
          variant={'standard'}
          control={control}
          fullWidth
          required
          options={[
            { label: 'Yes', id: 'true' },
            { label: 'No', id: 'false' },
          ]}
          onChange={(value) => {
            if (value !== 'true') {
              clearErrors('recipes');
            }
          }}
          sx={noUnderline}
        />
      }
    />
  );

  if (hasDemonstration) {
    rows.push(
      (i) => <CompleteRow
        n={i}
        key={`row-recipes`}
        name="recipes"
        colA="Recipe(s) Used"
        colB={session?.recipes || 'Not Applicable'}
        colC={
          <TextFieldElement
            id="field-recipes"
            name="recipes"
            variant={'standard'}
            control={control}
            required={watchDemo === 'true'}
            validation={{
              required: false,
              validate: {
                required: (value) => watchDemo !== 'true' || !!value || 'This field is required',
              }
            }}
            fullWidth
            sx={noUnderline}
          />
        }
      />
    );
  }

  rows.push(
    (i) => <CompleteRow
      n={i}
      key={`row-collaborators`}
      name="collaborators"
      colA="Collaborators"
      colB={session.collaborators.length
          ? session.collaborators.map(c => getUserName(c.email)).sort((a, b) => a.localeCompare(b)).join(', ')
          : 'None'}
      colC={
        <AutocompleteMultipleElement
          id="field-collaborators"
          name="collaborators"
          control={control}
          textFieldProps={{ variant: 'standard' }}
          fullWidth
          options={staffUserOptions}
          autocompleteProps={{
            sx: noUnderline,
            isOptionEqualToValue: (option, newValue) => {
              return option.id === newValue.id;
            }
          }}
        />
      }
    />
  );

  rows.push(
    (i) => <CompleteRow
      n={i}
      key={`row-donations_received`}
      name="donations_received"
      colA="External Donations Received"
      colB="Not Applicable"
      colC={
        <SelectElement
          id="field-donations_received"
          name="donations_received"
          variant={'standard'}
          control={control}
          fullWidth
          required
          options={[
            { label: 'Yes', id: 'true' },
            { label: 'No', id: 'false' },
          ]}
          sx={noUnderline}
        />
      }
    />
  );

  if (donationsReceived) {
    rows.push(
      (i) => <CompleteRow
        n={i}
        key={`row-donations_total`}
        name="donations_total"
        colA="Total Value of Donations Received"
        colB="Not Applicable"
        colC={
          <TextFieldElement
            id="field-donations_total"
            name="donations_total"
            variant={'standard'}
            control={control}
            inputRef={currencyFormatRef}
            fullWidth
            required
            sx={noUnderline}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
          />
        }
      />
    );
  }

  if (isClass) {
    rows.push(
      (i) => <CompleteRow
        n={i}
        key={`row-evaluation_tool`}
        name="evaluation_tool"
        colA="Evaluation Tool Distributed"
        colB="Not Applicable"
        colC={
          <SelectElement
            id="field-evaluation_tool"
            name="evaluation_tool"
            variant={'standard'}
            control={control}
            fullWidth
            required
            options={formQuestions?.evaluation_tool?.options ?? []}
            sx={noUnderline}
          />
        }
      />
    );
  }

  let rowCount = 0;

  const iterate = (rowItems, rowWrap = false, noBorder = false) => rowItems.map((row) => {
    rowCount += 1;
    const element = row(rowCount);
    const elements = (Array.isArray(element) ? element : [element]).filter((e) => e);

    const name = elements.reduce((acc, e) => {
      if (acc) {
        return acc;
      }
      if (e?.props?.name) {
        return e.props.name;
      }
    }, null);

    if (rowWrap) {
      return elements.map((el, i) => (
        <TableRow key={`table-row-${name}-${i}`} id={`table-row-${name}`}>
          <TableCell sx={noBorder ? { borderBottom: 'none' } : {}} className="question-cell">
            {el}
          </TableCell>
        </TableRow>
      ));
    }

    return elements.map((el, i) => (
      <Fragment key={`row-${name}-${i}`}>
        {el}
      </Fragment>
    ));
  });

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <FormContainer onSuccess={onSubmitClick('save')} FormProps={{ className: 'mui' }}>
          <TableComp>
            <TableBody>
              {iterate(topRows, true, true)}
            </TableBody>
          </TableComp>

          <TableComp>
            <TableHead>
              <TableHeadRow>
                <TableCell>Confirm Group Needs Identified and Actions Taken To Address Needs</TableCell>
              </TableHeadRow>
            </TableHead>
            <TableBody sx={{
              '& .question-cell:first-of-type': { paddingTop: '24px' },
            }}>
              {iterate(needsAndActions, true, true)}
            </TableBody>
          </TableComp>

          <TableComp>
            <TableHead>
              <TableHeadRow>
                <TableCell>Confirm Participant Details</TableCell>
              </TableHeadRow>
            </TableHead>
            <TableBody sx={{
              '& .question-cell:first-of-type': { paddingTop: '24px' },
            }}>
              {hasIndividualParticipants && (
                <TableRow>
                  <TableCell sx={{
                    borderBottom: "none"
                  }}>
                    <Alert severity="info" sx={{ position: 'relative' }}>
                      <Box sx={{
                        display: 'flex',
                        justifyContent: "space-between",
                        gap: 2
                      }}>
                        <Typography variant="body2">The number of participants for the session and demographic categories
                          have been pre-populated using participant data. If you need to make changes to these numbers,
                          you may update the session participant list by selecting the <strong>Update Participant
                          List</strong> button.</Typography>
                        <Button
                          variant="contained"
                          component={RouterLink}
                          onClick={handleUpdateParticipants}
                          sx={{
                            height: 40,
                            minWidth: 200,
                          }}
                          data-cy="button-edit-participants"
                        >
                          Update Participant List
                        </Button>
                      </Box>
                    </Alert>
                  </TableCell>
                </TableRow>
              )}
              {iterate(participantCountFields, true, true)}
            </TableBody>
          </TableComp>

          <TableComp>
            <TableHead>
              <TableHeadRow>
                <TableCell>Confirm Session Details</TableCell>
                <TableCell>Planned</TableCell>
                <TableCell sx={{ borderBottom: 'none' }}>Actual<span style={{ color: 'red' }}>*</span></TableCell>
              </TableHeadRow>
            </TableHead>
            <TableBody sx={{ '& .MuiTableCell-root:last-of-type': { width: '40%' } }}>
              {iterate(rows, false)}
            </TableBody>
          </TableComp>
        </FormContainer>

        {session.status !== 'COMPLETED' && (
          <>
            <Alert severity="warning" sx={{ marginBottom: '16px' }}>
              Once you mark a session as complete, it can no longer be changed or modified.
            </Alert>
            <Stack spacing={2} direction="row">
              <Button
                id="button-save"
                disabled={submitting}
                variant="contained"
                size="small"
                sx={{ marginTop: '20px' }}
                onClick={onSubmitClick('save')}
              >
                Save
              </Button>
              <Button
                id="button-submit"
                name="save"
                value="complete"
                disabled={submitting}
                variant="contained"
                size="small"
                sx={{ marginTop: '20px' }}
                onClick={onSubmitClick('complete')}
              >
                Save & Complete Session
              </Button>
            </Stack>
          </>
        )}

        {session.status === 'COMPLETED' && (
          <Stack spacing={2} direction="row">
            <Button
              id="button-submit"
              name="save"
              value="complete"
              disabled={submitting}
              variant="contained"
              size="small"
              sx={{ marginTop: '20px' }}
              onClick={onSubmitClick('update')}
            >
              Save
            </Button>
          </Stack>
        )}

      </LocalizationProvider>
    </>
  );
};

export default CompleteSessionForm;
